import CommonModal from "@/components/common/modal"; // Import the new CommonModal
import React, { useEffect, useRef, useState } from "react";
import { List, Badge, Button, Typography, Empty } from "antd";
import {
    DeleteOutlined,
    MessageOutlined,
    DownloadOutlined,
    SoundOutlined,
} from "@ant-design/icons";
import "@/css/common.css";
import styles from "./ChatRecordModal.css";
import { Api } from "@/api/api";
import mockChatMessages from "../mockData";
import { useLoading } from "@/components/global-loading";
import { FormattedMessage, injectIntl } from "react-intl";

const { Text, Title } = Typography;
const limitSize = 20;

const ChatRecordModal = ({ agentInfo, visible, onClose, intl }) => {
    const [listLoading, setListLoading] = useState(false);
    const [selectedChat, setSelectedChat] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [sessions, setSessions] = useState([]);
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [messages, setMessages] = useState([]);
    const pageRef = useRef(1);
    const audioElementRef = useRef(null);
    const playingAudioIdRef = useRef(null);
    const { showLoading, hideLoading } = useLoading();

    const loadSessions = () => {
        setListLoading(true);
        const params = {
            page: pageRef.current,
            limit: limitSize,
        };

        Api.agent.getAgentSessions(agentInfo?.id, params, (res) => {
            setListLoading(false);
            if (res?.data?.data?.list?.length) {
                const list = res.data.data.list;
                setHasMore(list.length === limitSize);

                setSessions((prevState) => [...prevState, ...list]);
                pageRef.current++;

                setCurrentSessionId((prevState) => {
                    if (prevState === null) {
                        setSelectedChat({
                            sessionId: list[0].sessionId,
                        });
                        return list[0].sessionId;
                    }

                    return prevState;
                });
            }
        });
    };

    useEffect(() => {
        if (agentInfo?.id) {
            loadSessions();
        }
    }, [agentInfo?.id]);

    const handleChatSelect = (chat) => {
        setSelectedChat(chat);
        setCurrentSessionId(chat.sessionId);
    };
    const handleDelete = (chatId, e) => {
        e.stopPropagation();
        console.log("删除对话:", chatId);
    };

    const onLoadMore = () => {
        setListLoading(true);
        setTimeout(() => {
            loadSessions();
        }, 1000);
    };

    useEffect(() => {
        if (currentSessionId) {
            getMessage();
        }
    }, [currentSessionId]);

    const getMessage = () => {
        showLoading(intl.formatMessage({ id: "gui.agentManage.loadingChatHistory", defaultMessage: "Loading chat history..." }));
        Api.agent.getAgentChatHistory(
            agentInfo?.id,
            currentSessionId,
            (res) => {
                hideLoading();
                if (res.data && res.data.data) {
                    const dataList = res.data.data;
                    setMessages(dataList);
                }
            }
        );
    };

    const loadMore = (
        <>
            {hasMore ? (
                <div
                    style={{
                        textAlign: "center",
                        marginTop: 12,
                        height: 32,
                        lineHeight: "32px",
                    }}
                >
                    <Button loading={listLoading} onClick={onLoadMore}>
                        <FormattedMessage id="gui.agentManage.loadMore" defaultMessage="Load More" />
                    </Button>
                </div>
            ) : (
                <div
                    style={{
                        color: "#666",
                        textAlign: "center",
                        padding: "10px 0",
                    }}
                >
                    <FormattedMessage id="gui.agentManage.noMore" defaultMessage="No more~" />
                </div>
            )}
        </>
    );

    const playAudio = (message) => {
        if (playingAudioIdRef.current === message.audioId) {
            // 如果正在播放当前音频，则停止播放
            if (audioElementRef.current) {
                audioElementRef.current.pause();
                audioElementRef.current = null;
            }
            playingAudioIdRef.current = null;
            return;
        }

        // 停止当前正在播放的音频
        if (audioElementRef.current) {
            audioElementRef.current.pause();
            audioElementRef.current = null;
        }

        // 先获取音频下载ID
        audioElementRef.current = message.audioId;
        Api.agent.getAudioId(message.audioId, (res) => {
            if (res.data && res.data.data) {
                // 使用获取到的下载ID播放音频
                audioElementRef.current = new Audio(
                    Api.getServiceUrl() + `/agent/play/${res.data.data}`
                );

                audioElementRef.current.onended = () => {
                    playingAudioIdRef.current = null;
                    audioElementRef.current = null;
                };

                audioElementRef.current.play();
            }
        });
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return "-";
        const date = new Date(timestamp);
        const now = new Date();
        const today = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate()
        );
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const hours = date.getHours().toString().padStart(2, "0");
        const minutes = date.getMinutes().toString().padStart(2, "0");

        if (date >= today) {
            return intl.formatMessage(
                { id: "gui.agentManage.todayTime", defaultMessage: "Today {hours}:{minutes}" },
                { hours, minutes }
            );
        } else if (date >= yesterday) {
            return intl.formatMessage(
                { id: "gui.agentManage.yesterdayTime", defaultMessage: "Yesterday {hours}:{minutes}" },
                { hours, minutes }
            );
        } else {
            const year = date.getFullYear();
            const month = (date.getMonth() + 1).toString().padStart(2, "0");
            const day = date.getDate().toString().padStart(2, "0");
            return `${year}-${month}-${day} ${hours}:${minutes}`;
        }
    };

    return (
        <CommonModal
            visible={visible}
            onClose={onClose}
            title={`${agentInfo?.name}-${intl.formatMessage({ id: "gui.agentManage.agent", defaultMessage: "Agent" })}`} // Title for the common modal
        >
            <div className={styles.chatContainer}>
                {/* 历史对话列表 */}
                <div className={styles.historyPanel}>
                    <div className={styles.historyHeader}>
                        <img
                            style={{
                                width: 25,
                                height: "auto",
                                marginRight: 10,
                            }}
                            src={require("@/assets/chatIcon1.png")}
                        />
                        <Title level={5} className={styles.headerTitle}>
                            <FormattedMessage id="gui.agentManage.chatHistoryTitle" defaultMessage="Chat History" />
                        </Title>
                    </div>

                    <List
                        className={`${styles.historyList} scroll-container`}
                        dataSource={sessions}
                        loadMore={loadMore}
                        renderItem={(item) => (
                            <List.Item
                                className={`${styles.historyItem} ${
                                    selectedChat?.sessionId === item.sessionId
                                        ? styles.selected
                                        : ""
                                }`}
                                onClick={() => handleChatSelect(item)}
                            >
                                <div className={styles.itemContent}>
                                    <div className={styles.itemHeader}>
                                        <Text className={styles.itemTitle}>
                                            {item.sessionName ||
                                                formatTime(item?.createdAt)}
                                            <Badge
                                                count={item.chatCount}
                                                className={styles.countBadge}
                                            />
                                            {/* {item.isActive && (
                                                <div
                                                    className={styles.activeDot}
                                                />
                                            )} */}
                                        </Text>
                                        {/* <div className={styles.itemActions}>
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={
                                                    <DeleteOutlined
                                                        style={{
                                                            fontSize: "20px",
                                                            color: "#FC6D26",
                                                        }}
                                                    />
                                                }
                                                className={styles.deleteBtn}
                                                onClick={(e) =>
                                                    handleDelete(item.id, e)
                                                }
                                            />
                                        </div> */}
                                    </div>
                                    <div className={styles.itemInfo}>
                                        <Text
                                            type="secondary"
                                            style={{ marginRight: 30 }}
                                            className={styles.deviceInfo}
                                        >
                                            <FormattedMessage id="gui.agentManage.device" defaultMessage="Device" />: {item.sessionDevice || "-"}
                                        </Text>
                                        <Text
                                            type="secondary"
                                            className={styles.macInfo}
                                        >
                                            {intl.formatMessage({ id: "gui.agentManage.macAddress", defaultMessage: "Mac Address" })}: {item.sessionMac || "-"}
                                        </Text>
                                    </div>
                                </div>
                            </List.Item>
                        )}
                    />
                </div>
                {/* 对话详情 */}
                <div className={styles.detailPanel}>
                    <div className={styles.detailHeader}>
                        <img
                            style={{
                                width: 25,
                                height: "auto",
                                marginRight: 10,
                            }}
                            src={require("@/assets/chatIcon2.png")}
                        />
                        <Title level={5} className={styles.headerTitle}>
                            <FormattedMessage id="gui.agentManage.chatDetail" defaultMessage="Chat Detail" />
                        </Title>
                    </div>
                    <div className={styles.chatContent}>
                        {messages.map((message, index) => (
                            <div key={index} className={styles.messageGroup}>
                                <div
                                    className={`${styles.message} ${
                                        message.chatType === 1
                                            ? styles.userMessage
                                            : styles.aiMessage
                                    }`}
                                >
                                    <div className={styles.messageHeader}>
                                        <Text className={styles.messageSender}>
                                            {message.chatType === 1
                                                ? intl.formatMessage({ id: "gui.agentManage.user", defaultMessage: "User" })
                                                : intl.formatMessage({ id: "gui.agentManage.assistant", defaultMessage: "AI" })}
                                        </Text>
                                        <div className={styles.messageActions}>
                                            {message.chatType === 1 && (
                                                <>
                                                    <Button
                                                        onClick={() =>
                                                            playAudio(message)
                                                        }
                                                        disabled={
                                                            !message?.audioId
                                                        }
                                                        type="text"
                                                        size="small"
                                                        icon={
                                                            <SoundOutlined
                                                                style={{
                                                                    color: !message?.audioId
                                                                        ? "#aaa"
                                                                        : "#5F2EBF",
                                                                    fontSize: 20,
                                                                }}
                                                            />
                                                        }
                                                    />
                                                    {/* <Button
                                                        type="text"
                                                        size="small"
                                                        icon={
                                                            <DownloadOutlined
                                                                style={{
                                                                    color: "#5F2EBF",
                                                                    fontSize: 20,
                                                                }}
                                                            />
                                                        }
                                                    /> */}
                                                </>
                                            )}
                                            <Text
                                                type="secondary"
                                                className={styles.messageTime}
                                            >
                                                {message.createdAt}
                                            </Text>
                                        </div>
                                    </div>

                                    <div className={styles.messageContent}>
                                        {message.content}
                                    </div>

                                    {message.source && (
                                        <div className={styles.messageSource}>
                                            <Text
                                                type="secondary"
                                                className={styles.sourceText}
                                            >
                                                {message.source}
                                            </Text>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        {!messages?.length && (
                            <div className="empty-message">
                                <Empty description={intl.formatMessage({ id: "gui.agentManage.noChatData", defaultMessage: "No chat data" })} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </CommonModal>
    );
};

export default injectIntl(ChatRecordModal);